import context from '@truckdown/components-systems';
import { Listing } from '@truckdown/locations';
import { stringify } from 'querystring';
import { defineComponent, ref, Ref, computed, onMounted, watch, nextTick } from 'vue';

interface MapLocation {
    lat: number;
    lng: number;
}

interface MapPin {
    id: string;
    label: string;
    listing?: Listing;
    address: string;
    address2?: string;
    location: MapLocation;
    icon?: string;
    allowAction: boolean;
    actionTitle: string;
}

interface MapDisplayModel {
    searchPoint?: MapPin;
    pins: MapPin[];
    listingIds: string[];
    selectedPinId?: string;
}

interface IProps {
    displayOptions: MapDisplayModel
}

interface IData {
    openedMarker: Ref<string>,
    mapPins: Ref<MapPin[]>,
    gMap: Ref<null | HTMLElement>,
    gMapInstance: Ref<google.maps.Map | null>
}

const getData = function (props: IProps) {
    let pins = props.displayOptions.pins;
    if (props.displayOptions.searchPoint) {
        pins.push(props.displayOptions.searchPoint);
    }
    return {
        openedMarker: ref<string>(props.displayOptions.selectedPinId ?? ''),
        mapPins: ref<MapPin[]>(pins),
        gMap: ref<null | HTMLElement>(null),
        gMapInstance: ref<google.maps.Map | null>(null)
    } as IData;
}

const defaultCenter = { lat: 49.8954, lng: -97.1385 };
const defaultZoom = 7;
const singleZoom = 11;

const getComputed = function (data: IData, props: IProps) {
    return {
        center: computed(() => {
            return getCenter(props.displayOptions);
        }),

        zoom: computed(() => {
            return defaultZoom;
        })
    };
}

const getCenter = function (model: MapDisplayModel) {
    if (model.searchPoint) {
        return model.searchPoint.location;
    }

    if (model.pins.length > 0) {
        return getCenterBasedOnLocations(model.pins.map((l) => l.location));
    }

    return defaultCenter;
}

const getCenterBasedOnLocations = function (locations: MapLocation[]) {
    let lat = 0;
    let lng = 0;
    let length = locations.length;

    if (length == 0) {
        return defaultCenter;
    }

    for (let i = 0; i < locations.length; ++i) {
        lat += locations[i].lat;
        lng += locations[i].lng;
    }

    lat /= length;
    lng /= length;

    return { lat: lat, lng: lng };
}

const setMapBounds = function (props: IProps, data: IData, center: MapLocation, map: google.maps.Map) {
    data.gMapInstance.value = map;

    console.log('Setting map bounds', props.displayOptions);

    if (props.displayOptions.pins.length == 1) {
        nextTick(() => {
            map.setZoom(singleZoom);
        });
    } else if (props.displayOptions.pins.length > 1) {
        var bounds = new google.maps.LatLngBounds();
        for (let i = 0; i < props.displayOptions.pins.length; i++) {
            bounds.extend({ lat: props.displayOptions.pins[i].location.lat, lng: props.displayOptions.pins[i].location.lng });
        }
        bounds.extend(center);
        nextTick(() => {
            map.setZoom(singleZoom);
            map.fitBounds(bounds);
        });
    }
}

interface DragModel {
    id: string;
    loc: MapLocation;
}

let pinActions: string[] = [];
let pinDrags: DragModel[] = [];


const getMethods = function (data: IData, props: IProps) {
    return {
        openMarker: function (id: string) {
            if (data.openedMarker.value == id) {
                data.openedMarker.value = '';
            } else {
                data.openedMarker.value = id;
            }
        },
        closeMarker: function (id: string) {
            if (data.openedMarker.value == id) {
                data.openedMarker.value = '';
            }
        },
        dragEnd: function (evt: any, pin: MapPin) {
            console.log(evt);
            pinDrags.push({
                id: pin.id,
                loc: {
                    lat: evt.latLng.lat(),
                    lng: evt.latLng.lng()
                }
            })
        },
        pinAction: function (id: string) {
            pinActions.push(id);
        }
    };
}

export default defineComponent({
    name: 'dynamic-map',
    props: {
        displayOptions: {
            type: Object as () => MapDisplayModel,
            required: true
        }
    },
    emits: [],
    setup: function (props, ctx) {
        let data = getData(props);
        let computed = getComputed(data, props);
        let methods = getMethods(data, props);

        onMounted(() => {
            (window as any).getActions = function () {
                let res = JSON.stringify(pinActions);
                pinActions = [];
                return JSON.parse(res);
            };

            (window as any).getDrags = function () {
                let res = JSON.stringify(pinDrags);
                pinDrags = [];
                return JSON.parse(res);
            };

            (window as any).openMarker = function (id: string) {
                data.openedMarker.value = id;
                return id;
            };

            (window as any).zoomMap = function () {
                if (data.gMapInstance.value != null) {
                    setMapBounds(props, data, computed.center.value, data.gMapInstance.value);
                    return 'ok';
                }
                return 'no';
            };

            if (data.gMap.value != null) {
                let mapLoader = (data.gMap.value as any).$mapPromise as Promise<google.maps.Map>;
                if (mapLoader) {
                    mapLoader.then((map) => {
                        data.gMapInstance.value = map;
                        setMapBounds(props, data, computed.center.value, map);
                    });
                }
            } else {
                console.log('No map element');
            }
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});